import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Home, LayoutDashboard, PlusCircle, BookOpen, User } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/dashboard', icon: LayoutDashboard, label: 'My Trips', auth: true },
  { path: '/new-trip', icon: PlusCircle, label: 'New Trip', auth: true, accent: true },
  { path: '/journal', icon: BookOpen, label: 'Journal', auth: true },
  { path: '/profile', icon: User, label: 'Profile', auth: true },
];

export const MobileBottomNav = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  // Hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        if (currentY > lastScrollY.current && currentY > 80) {
          setVisible(false); // scrolling down
        } else {
          setVisible(true); // scrolling up
        }
        lastScrollY.current = currentY;
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isAuthenticated) return null;

  const items = NAV_ITEMS.filter((item) => !item.auth || isAuthenticated);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      data-testid="mobile-bottom-nav"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="bg-white/95 backdrop-blur-xl border-t border-gray-200/60 shadow-[0_-2px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-around px-2 py-1.5">
          {items.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 min-w-[56px] min-h-[48px] rounded-xl transition-all duration-200 ${
                  item.accent && !active
                    ? 'text-white'
                    : active
                    ? 'text-ocean'
                    : 'text-gray-400 active:text-gray-600'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {item.accent ? (
                  <div
                    className={`w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 ${
                      active
                        ? 'bg-ocean scale-110 shadow-ocean/30'
                        : 'bg-gradient-to-br from-ocean to-sky active:scale-95'
                    }`}
                  >
                    <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                  </div>
                ) : (
                  <>
                    <div className={`transition-transform duration-200 ${active ? 'scale-110' : ''}`}>
                      <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 1.8} />
                    </div>
                    <span className={`text-[10px] leading-tight font-medium ${active ? 'font-semibold' : ''}`}>
                      {item.label}
                    </span>
                    {active && (
                      <div className="w-1 h-1 rounded-full bg-ocean mt-0.5" />
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
